import { Component, OnInit, EventEmitter, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { take, takeUntil } from 'rxjs/operators';
import { ProjectChecklist, ProjectChecklistColumn, ProjectChecklistRow, ProjectInfo } from '../../../models/project/project-info.model';
import { CommunicationService } from '../../../communication/communication.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { saveAs } from 'file-saver';
import { v4 as uuid } from 'uuid';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-scoping-results',
  templateUrl: './scoping-results.component.html',
  styleUrls: ['./scoping-results.component.scss']
})

export class ProjectScopingResultsComponent extends UserpilotReloader implements OnInit, OnDestroy {
  checklists: ProjectChecklist[] = [];
  originalList: ProjectChecklist[] = [];
  projectInfo: ProjectInfo;
  scopingTestList: ProjectChecklist = null;
  testListCopy: ProjectChecklist = null;

  selected: ProjectChecklist;
  flattenedRows: ProjectChecklistRow[];
  selectedReportRows: ProjectChecklistRow[];
  maxLevelShown;

  onDestroyNotifier = new EventEmitter();
  dataChangeNotifier = new EventEmitter();

  loaded = false;

  isSavedProject = false;

  trigger: string;
  constructor(
    private router: Router,
    private translationService: AnessaTranslationService,
    private comService: CommunicationService,
    private toastrService: ConfiguredToastrService,
    private projectCommService: ProjectCommunicationService
  ) {
    super();
  }


  ngOnInit() {
    this.projectCommService.projectFullyLoadedNotifier.pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((loaded) => {
        this.loaded = loaded;
        if (loaded) {
          this.projectInfo = this.projectCommService.getProjectInfo();
          this.isSavedProject = !!this.projectCommService.getProjectId();
          this.checklists = this.projectInfo.checklists;
          this.originalList = ProjectChecklist.arrayDeepCopy(this.checklists);
          if (this.projectInfo.scopingTests) {
            this.scopingTestList = this.projectInfo.scopingTests;
            this.testListCopy = ProjectChecklist.deepCopy(this.scopingTestList);
          } else {
            this.createDummyTestList();
            this.testListCopy = ProjectChecklist.deepCopy(this.scopingTestList);
          }
        }
      });

    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
    // unload project to prevent the limited users from wandering around the actual software. Except when going to initial form
    if (this.router.routerState.snapshot.url.indexOf('project-input') === -1) {
      this.projectCommService.unloadProject();
    }
  }

  onSave(checklist) {
    // the call is byReference anyway, so the checklist object doesn't have to be re-set
    this.selected = undefined;
    this.projectInfo.scopingTests = this.scopingTestList;
    this.projectInfo.checklists = this.checklists;
    this.projectCommService.setScopingInfo(this.projectInfo);
    this.originalList = ProjectChecklist.arrayDeepCopy(this.projectInfo.checklists);
    this.testListCopy = ProjectChecklist.deepCopy(this.projectInfo.scopingTests);
  }

  private createDummyTestList() {
    this.scopingTestList = new ProjectChecklist();
    this.scopingTestList.created = new Date();
    this.scopingTestList.name = 'tests';
    // Default columns
    this.scopingTestList.columns = [
      new ProjectChecklistColumn({
        uuid: uuid(), name: this.translationService.get('parameter/criteria'), acceptedItems: [], cellDropdownItems: {}, locked: true
      }),
      new ProjectChecklistColumn({
        uuid: uuid(), name: this.translationService.get('value'), acceptedItems: [], cellDropdownItems: {}, locked: false
      }),
      new ProjectChecklistColumn({
        uuid: uuid(), name: this.translationService.get('unit'), acceptedItems: [], cellDropdownItems: {}, locked: false
      }),
      new ProjectChecklistColumn({
        uuid: uuid(), name: this.translationService.get('indicator'), acceptedItems: ['#FF0000', '#FF681F', '#FFFF00', '#00FF00'], cellDropdownItems: {}, locked: false
      }),
      new ProjectChecklistColumn({
        uuid: uuid(), name: this.translationService.get('suitability'), acceptedItems: [], cellDropdownItems: {}, locked: false
      }),
      new ProjectChecklistColumn({
        uuid: uuid(), name: this.translationService.get('anessa\'s comments'), acceptedItems: [], cellDropdownItems: {}, locked: false
      })
    ];

    const rowData = [
      { "parameter/criteria": "Annual biomethane production",              "id": "1", "value": "", "unit": "", "indicator": "", "suitability": "", "anessa's comments": "" },
      { "parameter/criteria": "CAPEX (excluding interconnection)",         "id": "2", "value": "", "unit": "", "indicator": "", "suitability": "", "anessa's comments": "" },
      { "parameter/criteria": "OPEX divided by CAPEX",                     "id": "3", "value": "", "unit": "", "indicator": "", "suitability": "", "anessa's comments": "" },
      { "parameter/criteria": "IRR",                                       "id": "4", "value": "", "unit": "", "indicator": "", "suitability": "", "anessa's comments": "" },
      { "parameter/criteria": "Distance to interconnection",               "id": "5", "value": "", "unit": "", "indicator": "", "suitability": "", "anessa's comments": "" },
      { "parameter/criteria": "CI score",                                  "id": "6", "value": "", "unit": "", "indicator": "", "suitability": "", "anessa's comments": "" },
      { "parameter/criteria": "Solid digestate production",                "id": "7", "value": "", "unit": "", "indicator": "", "suitability": "", "anessa's comments": "" },
      { "parameter/criteria": "Period before the targeted injection date", "id": "8", "value": "", "unit": "", "indicator": "", "suitability": "", "anessa's comments": "" }
    ];
    for (let i = 0; i < rowData.length; i++) {
      const row = new ProjectChecklistRow();
      row.data = rowData[i];
      this.scopingTestList.rows.push(row);
    }
  }

  goBack() {
    this.router.navigateByUrl('/project-input');
  }

  getIndicatorStatus() {
    if (this.scopingTestList.rows && this.scopingTestList.rows.length > 0) {
      for (const row of this.scopingTestList.rows) {
        console.log(row.data);
      }
    } else {
      return ['#BBBBBB'];
    }

  }

  flattenChecklistRows(rows: ProjectChecklistRow[], parentIndex?: string) {
    const flattenedRows: ProjectChecklistRow[] = [];
    rows.forEach((row, index) => {
      let dotIndex: string;
      if (parentIndex) {
        dotIndex = `${parentIndex}.${String(index + 1)}`;
      } else {
        dotIndex = String(index + 1);
      }
      row.data.id = dotIndex;
      flattenedRows.push(row);
      // Only include rows up to the overall selected level
      if (row.level < this.maxLevelShown) {
        flattenedRows.push(...this.flattenChecklistRows(row.children, dotIndex));
      }
    });
    return flattenedRows;
  }

  getMaxLevel() {
    let maxLevel = 0;
    const recFunc = (rows: ProjectChecklistRow[]) => {
      for (const row of rows) {
        if (row.level > maxLevel) {
          maxLevel = row.level;
        }
        if (row.children.length > 0) {
          recFunc(row.children);
        }
      }
    };
    recFunc(this.selected.rows);
    return maxLevel;
  }

  getNameForRow(row: ProjectChecklistRow) {
    if (!row?.data) {
      return '-';
    }
    const keys = Object.keys(row.data);
    if (keys.length < 1) {
      return 'no data';
    }
    // taking the first key as the main name of the row
    return row.data[keys[0]];
  }

  findSelectedRows(rows: ProjectChecklistRow[]) {
    for (const row of rows) {
      if (row.starred) {
        this.selectedReportRows.push(row);
      }
      if (row.children.length > 0) {
        this.findSelectedRows(row.children);
      }
    }
  }

  onSelectedChecklistChange() {
    // iterate over rows and mark the ones starred as selected for report
    if (!this.selected) {
      return;
    }
    this.selectedReportRows = [];
    this.findSelectedRows(this.selected.rows);
    this.maxLevelShown = this.getMaxLevel();
    this.flattenedRows = this.flattenChecklistRows(this.selected.rows);
  }

  /**
   * Recursive function to go through a tree of rows and mark all selected rows as starred and unselected ones as not starred
   * @param rows
   * @returns
   */
  private starSelectedRows(rows: ProjectChecklistRow[]) {
    if (!rows || rows.length === 0) {
      return;
    }

    for (const row of rows) {
      row.starred = this.selectedReportRows.includes(row);
      if (row.children.length > 0) {
        this.starSelectedRows(row.children);
      }
    }

  }

  onSelectedRowsChange() {
    if (this.selected && this.selected.rows) {
      this.starSelectedRows(this.selected.rows);
    }
  }

  onCancel() {
    this.selected = null;
    this.scopingTestList = ProjectChecklist.deepCopy(this.testListCopy);
    this.checklists = ProjectChecklist.arrayDeepCopy(this.originalList);
    this.onSelectedChecklistChange();
  }

  getReport() {
    if (this.projectCommService.getProjectId()) {
      this.comService.getRequest(this.projectCommService.getProjectId(), '/dbapi/report-documents')
        .pipe(take(1)).subscribe(res => {
          if (res) {
            const reportDoc = res['obj'];
            this.comService.fileRequest({ id: reportDoc['_id'] }, '/dbapi/report-documents/getReport')
              .pipe(take(1)).subscribe(doc => {
                if (doc) {
                  const fileName = reportDoc['name'];
                  saveAs(doc, fileName);
                }
              });
          } else {
            this.toastrService.warning('anessa is in the process of analyzing the project. Please check back later.');
          }
        });
    }
  }

  /** Table Functions */
  getListHeaders(list: ProjectChecklist) {
    const headers = [];
    if (list && list.columns) {
      for (const col of list.columns) {
        if (!col.isIcon) headers.push(col.name);
      }
    }
    return headers;
  }

  getDataSource(list: ProjectChecklist) {
    const data = [];
    if (list && list.rows) {
      for (const row of list.rows) {
        if (row.starred) {
          data.push(row.data);
        }
        if (row.children.length > 0) {
          data.push(...this.getChildData(row.children));
        }
      }
    }
    return data;
  }

  getChildData(rows: ProjectChecklistRow[]) {
    const data = [];
    for (const row of rows) {
      if (row.starred) {
        data.push(row.data);
      }
      if (row.children.length > 0) {
        data.push(...this.getChildData(row.children));
      }
    }
    return data;
  }

  isColourHexCode(value: string) {
    if (value != null && String(value).startsWith('#')) {
      return true;
    }
    return false;
  }
  /** End Table Functions */
}
